// Tiny DOM helpers shared by every UI module. No framework.

export const $ = (sel, root = document) => root.querySelector(sel);
export const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

const SVG_NS = 'http://www.w3.org/2000/svg';

function applyProps(el, props) {
  if (!props) return;
  for (const [k, v] of Object.entries(props)) {
    if (v === undefined || v === null || v === false) continue;
    if (k === 'class') el.setAttribute('class', v);
    else if (k === 'text') el.textContent = v;
    else if (k === 'html') el.innerHTML = v;
    else if (k === 'style' && typeof v === 'object') Object.assign(el.style, v);
    else if (k === 'dataset') Object.assign(el.dataset, v);
    else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v);
    else if (k === 'value' && 'value' in el) el.value = v;
    else if (k === 'checked' || k === 'disabled' || k === 'selected' || k === 'hidden') el[k] = !!v;
    else el.setAttribute(k, v === true ? '' : String(v));
  }
}

function append(el, children) {
  for (const c of children.flat(Infinity)) {
    if (c === null || c === undefined || c === false) continue;
    el.appendChild(c instanceof Node ? c : document.createTextNode(String(c)));
  }
}

/** h('div', { class: 'x', onClick }, child, 'text') */
export function h(tag, props, ...children) {
  const el = document.createElement(tag);
  applyProps(el, props);
  append(el, children);
  return el;
}

/** SVG element with attributes (numbers are stringified). */
export function s(tag, attrs, ...children) {
  const el = document.createElementNS(SVG_NS, tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v === undefined || v === null || v === false) continue;
      if (k === 'class') el.setAttribute('class', v);
      else if (k === 'text') el.textContent = v;
      else if (k === 'dataset') Object.assign(el.dataset, v);
      else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v);
      else el.setAttribute(k, String(v));
    }
  }
  append(el, children);
  return el;
}

export function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

// ----- Units ----------------------------------------------------------------
/** file units (1/100 mm) -> "12.34" */
export const mm = (units, digits = 2) => (Number(units || 0) / 100).toFixed(digits);
/** "12.34" (mm) -> 1234 units, or null when not a number */
export function unitsFromMm(text) {
  const n = parseFloat(String(text).replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}
/** Printer dot at 305 dpi, in file units (1 dot = 8.33 units). */
export const DOT = 2540 / 305;

// ----- Platform / keyboard labels --------------------------------------------
const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || '';
export const isMac = /mac|iphone|ipad|ipod/i.test(platform);

const KEY_NAMES = isMac
  ? { mod: '⌘', shift: '⇧', alt: '⌥', enter: '↵', delete: 'Del', esc: 'Esc' }
  : { mod: 'Ctrl', shift: 'Shift', alt: 'Alt', enter: 'Enter', delete: 'Del', esc: 'Esc' };

/** 'Mod+Shift+Z' -> ['⌘','⇧','Z'] or ['Ctrl','Shift','Z'] */
export function kbdKeys(combo) {
  return String(combo)
    .split('+')
    .filter(Boolean)
    .map((k) => KEY_NAMES[k.toLowerCase()] || k);
}
/** 'Mod+S' -> '⌘S' (mac) / 'Ctrl+S' */
export function kbd(combo) {
  const keys = kbdKeys(combo);
  return isMac ? keys.join('') : keys.join('+');
}

/** Fill [data-kbd] text and [data-kbd-title="Label|Mod+S"] tooltips in static markup. */
export function applyKbdLabels(root = document) {
  for (const el of $$('[data-kbd]', root)) el.textContent = kbd(el.dataset.kbd);
  for (const el of $$('[data-kbd-title]', root)) {
    const [label, combo] = el.dataset.kbdTitle.split('|');
    el.title = combo ? `${label} (${kbd(combo)})` : label;
  }
}

// ----- Misc -----------------------------------------------------------------
export function debounce(fn, ms) {
  let t = 0;
  const d = (...a) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...a), ms);
  };
  d.flush = (...a) => {
    clearTimeout(t);
    fn(...a);
  };
  return d;
}

export function ago(ts) {
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86400)}d`;
}

export function toast(message, kind = '') {
  const stack = document.getElementById('toast-stack');
  if (!stack) return;
  const el = h('div', { class: `toast ${kind}`, role: kind === 'bad' ? 'alert' : 'status' }, message);
  stack.appendChild(el);
  setTimeout(() => el.classList.add('fade'), kind === 'bad' ? 4200 : 2400);
  setTimeout(() => el.remove(), kind === 'bad' ? 4600 : 2800);
}

/**
 * Modal confirm. body: Node | string. Resolves true (ok) / false (cancel, Esc, backdrop).
 */
export function confirmDialog({ title, body, okLabel = 'OK', cancelLabel = 'Cancel', danger = false }) {
  const ov = document.getElementById('dialog-overlay');
  const prevFocus = document.activeElement;
  return new Promise((resolve) => {
    const done = (v) => {
      ov.classList.remove('visible');
      ov.replaceChildren();
      document.removeEventListener('keydown', onKey, true);
      if (prevFocus && prevFocus.focus) prevFocus.focus();
      resolve(v);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        done(false);
      } else if (e.key === 'Tab') {
        const f = $$('button', ov);
        const i = f.indexOf(document.activeElement);
        e.preventDefault();
        f[(i + (e.shiftKey ? -1 : 1) + f.length) % f.length].focus();
      }
    };
    const cancel = h('button', { class: 'btn', onClick: () => done(false) }, cancelLabel);
    const ok = h('button', { class: 'btn primary', style: danger ? { background: 'var(--bad)' } : null, onClick: () => done(true) }, okLabel);
    const card = h(
      'div',
      { class: 'overlay-card' },
      h(
        'div',
        { class: 'dpi-ruler' },
        h('div', { class: 'dpi-headline', id: 'dialog-title' }, title),
        typeof body === 'string' ? h('div', { class: 'dpi-sub', style: { marginBottom: '0' } }, body) : body,
      ),
      h('div', { class: 'dpi-actions' }, cancel, ok),
    );
    ov.replaceChildren(card);
    ov.onmousedown = (e) => {
      if (e.target === ov) done(false);
    };
    ov.classList.add('visible');
    document.addEventListener('keydown', onKey, true);
    setTimeout(() => cancel.focus(), 20);
  });
}
